/*
 * NOTE: sync with `modules/analytics/src/types/ClickstreamContextualArgsTypes.ts`
 * to avoid type mismatch for ts-typecheck
 */
import type { RentGuaranteeExperimentVariation } from '@zg-rentals/rental-manager-env';
import type { RENTAL_PROTECTION_PAYMENT_OPTIONS_REQUEST_VALUE } from '../constants/RentGuaranteeConstants';

type TagModel = {
  appearance?: string;
  icon?: string;
  text?: string;
};

type ModularListingBannerModel = {
  bannerDescription?: string;
  bannerTitle?: string;
  ctaText?: string;
  tag?: TagModel;
};

type AdditionalDetailsModel = {
  isMultiPolicyBannerOnWebEnabled?: boolean;
  isEligibleForLowFrictionOptIn?: boolean;
  urls?: {
    faq?: string;
    samplePolicy?: string;
    coverageSummary?: string;
  };
};

// Gets object values as type
// https://stackoverflow.com/a/53662389
type PaymentOptionRequestKeys = keyof typeof RENTAL_PROTECTION_PAYMENT_OPTIONS_REQUEST_VALUE;
type PaymentOptionRequestValueType = (typeof RENTAL_PROTECTION_PAYMENT_OPTIONS_REQUEST_VALUE)[PaymentOptionRequestKeys];

export type RentalProtectionExperimentDetail = {
  originalRentPrice: number;
  originalSecurityDeposit: number;
  variation: keyof typeof RentGuaranteeExperimentVariation;
  selectedOption: PaymentOptionRequestValueType;
};

export type PremiumCoverage = {
  premium: number;
  coverage: number;
  isDefault: boolean;
  isSelected: boolean;
  premiumCoverageId: number;
};

const Tag = ({ appearance = '', icon = '', text = '' }: TagModel = {}) => ({
  appearance: String(appearance),
  icon: String(icon),
  text: String(text),
});

const ModularListingBanner = ({
  bannerDescription = '',
  bannerTitle = '',
  ctaText = '',
  tag = {},
}: ModularListingBannerModel = {}) => ({
  bannerDescription: String(bannerDescription),
  bannerTitle: String(bannerTitle),
  ctaText: String(ctaText),
  tag: Tag(tag),
});

const AdditionalDetails = ({
  isMultiPolicyBannerOnWebEnabled = false,
  isEligibleForLowFrictionOptIn = false,
  urls: { faq = '', samplePolicy = '', coverageSummary = '' } = {},
}: AdditionalDetailsModel = {}) => ({
  isMultiPolicyBannerOnWebEnabled: Boolean(isMultiPolicyBannerOnWebEnabled),
  isEligibleForLowFrictionOptIn: Boolean(isEligibleForLowFrictionOptIn),
  urls: { faq, samplePolicy, coverageSummary },
});

export type RentGuaranteeProps = {
  cost: number;
  coverage: number;
  isDeferred: boolean;
  isEligible: boolean;
  isEnabled: boolean;
  isEnabledInitialValue: boolean;
  isSecurityDepositWaived: boolean;
  isSecurityDepositReviewed: boolean;
  modularListingBanner: ModularListingBannerModel;
  premium: number;
  hasRecentlyStartedRequest: boolean;
  securityDeposit: number;
  zuid: string;
  isInterestedInLearningMore: string;
  experimentsDetails: Array<RentalProtectionExperimentDetail>;
  additionalDetails: AdditionalDetailsModel;
  premiumCoverages: Array<PremiumCoverage>;
};

const RentGuarantee = ({
  cost = 30,
  coverage = 0,
  isDeferred = false,
  isEligible = false,
  isEnabled = false,
  isSecurityDepositWaived = false,
  isSecurityDepositReviewed = false,
  modularListingBanner = {},
  premium = 45,
  hasRecentlyStartedRequest = false,
  securityDeposit = 0,
  zuid = '',
  isInterestedInLearningMore = '',
  experimentsDetails = [],
  additionalDetails = {},
  premiumCoverages = [],
}: Partial<RentGuaranteeProps> = {}): RentGuaranteeProps => ({
  cost: Number(cost),
  coverage: Number(coverage),
  isDeferred: Boolean(isDeferred),
  isEligible: Boolean(isEligible),
  isEnabled: Boolean(isEnabled),
  isEnabledInitialValue: Boolean(isEnabled),
  isSecurityDepositWaived: Boolean(isSecurityDepositWaived),
  isSecurityDepositReviewed: Boolean(isSecurityDepositReviewed),
  modularListingBanner: ModularListingBanner(modularListingBanner),
  premium: Number(premium),
  hasRecentlyStartedRequest: Boolean(hasRecentlyStartedRequest),
  securityDeposit: Number(securityDeposit),
  zuid: String(zuid),
  isInterestedInLearningMore: String(isInterestedInLearningMore),
  experimentsDetails,
  additionalDetails: AdditionalDetails(additionalDetails),
  premiumCoverages,
});

export default RentGuarantee;
